import React from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {Icon, type IconName} from '@/shared/ui/icons';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';

/**
 * The KPI tile — one current number, and nothing else in the box.
 *
 * Это `Stat` ui-kit — приборная шкала: цветная риска слева, подпись 12px
 * приглушённо, крупное моноширинное число, строка «из чего сложилось». Та же
 * плитка, что в `StatBar`, плюс необязательный значок в углу, шкала-доля и тренд.
 *
 * The contract is fixed: label · value · optional hint · optional meter or trend.
 * Nothing else goes in a tile.
 *
 * ## Tone is state, and there is no other kind
 *
 * A tile reports how something IS, so its accents come from the status palette:
 * `good` / `warn` / `bad`, plus `info` — синий, для главного числа без тревоги:
 * акцент красный, и красное число на пульте читается как беда. Риска слева
 * повторяет цвет числа: ряд плиток читается по краю, до чтения самих цифр.
 */
export type StatTone = 'neutral' | 'good' | 'warn' | 'bad' | 'info';

type ToneSkin = {wash: string; glyph: string; mark: string; track: string};

function skin(p: Palette, tone: StatTone): ToneSkin {
  switch (tone) {
    case 'info':
      return {
        wash: p.infoSoft,
        glyph: p.infoInk,
        mark: p.info,
        track: withAlpha(p.info, 0.2),
      };
    case 'good':
      return {
        wash: withAlpha(p.signal, 0.12),
        glyph: p.signalInk,
        mark: p.signal,
        track: withAlpha(p.signal, 0.2),
      };
    case 'warn':
      return {
        wash: withAlpha(p.warning, 0.12),
        glyph: p.warningInk,
        mark: p.warning,
        track: withAlpha(p.warning, 0.22),
      };
    case 'bad':
      return {
        wash: withAlpha(p.destructive, 0.12),
        glyph: p.destructiveInk,
        mark: p.destructive,
        track: withAlpha(p.destructive, 0.2),
      };
    case 'neutral':
      return {
        wash: p.neutralSoft,
        glyph: p.mutedForeground,
        mark: p.borderStrong,
        track: withAlpha(p.mutedForeground, 0.2),
      };
  }
}

/**
 * 1 284 → "1 284", 12 934 → "12,9 тыс.". A tile's value is read at a glance; past four
 * digits the exact figure is noise, and the list behind the tile is where to go for it.
 */
export function compactNumber(n: number, locale = 'ru-RU'): string {
  if (!Number.isFinite(n)) {
    return '—';
  }
  if (Math.abs(n) < 10_000) {
    return n.toLocaleString(locale);
  }
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

const styles = StyleSheet.create({
  /* Та же плитка, что у `StatBar`: риска слева, подпись, моноширинное число. */
  tile: {
    flex: 1,
    minWidth: 0,
    gap: 2,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderLeftWidth: 3,
    paddingLeft: spacing(3) - 2,
    paddingRight: spacing(3),
    paddingVertical: spacing(2),
    overflow: 'hidden',
  },
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), minHeight: 18},
  label: {flex: 1, minWidth: 0},
  figures: {flexDirection: 'row', alignItems: 'flex-end', gap: spacing(2)},
  value: {flex: 1, minWidth: 0},
  figure: {flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap'},
  unit: {marginLeft: 4, marginRight: 6},
  hint: {marginTop: 1},
  /* Полоса выполнения ui-kit: 6pt, прямоугольная, на `surface-inset` с тонкой рамкой. */
  meter: {height: 6, borderRadius: radii.xs, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden', marginTop: spacing(1)},
  meterFill: {height: '100%'},
  trend: {flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 26},
  bar: {width: 4, minHeight: 2},
});

/**
 * A ratio against its whole. The track is a lighter step of the fill's own ramp, so the
 * bar reads as one object at one state rather than as two colours meeting in the
 * middle.
 */
export function Meter({
  value,
  max,
  tone = 'info',
  style,
}: {
  value: number;
  max: number;
  tone?: StatTone;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const t = skin(p, tone);
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <View
      accessibilityRole="progressbar"
      accessibilityValue={{min: 0, max: 100, now: Math.round(pct)}}
      style={[styles.meter, {backgroundColor: p.inset, borderColor: p.borderSubtle}, style]}>
      <View style={[styles.meterFill, {backgroundColor: t.mark, width: `${pct}%`}]} />
    </View>
  );
}

/**
 * A twelve-point trend, drawn beside the number.
 *
 * A sparkline answers "where is this heading", which is a question about the right-hand
 * end — so only the last bar takes the tone and the run behind it is the de-emphasis
 * grey. There is no SVG in this app (one sparkline does not justify the dependency), and
 * bars carry that reading without the dependency.
 */
export function Sparkbars({
  points,
  tone = 'info',
  style,
}: {
  points: number[];
  tone?: StatTone;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const t = skin(p, tone);
  if (points.length < 2) {
    return null;
  }
  const max = Math.max(...points, 1);
  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[styles.trend, style]}>
      {points.map((v, i) => (
        <View
          key={i}
          style={[
            styles.bar,
            {
              height: Math.max(2, Math.round((v / max) * 30)),
              backgroundColor:
                i === points.length - 1 ? t.mark : withAlpha(p.mutedForeground, 0.35),
            },
          ]}
        />
      ))}
    </View>
  );
}

/**
 * A value, printed as figures and units rather than as one string.
 *
 * `8 ч 30 мин` set entirely at metric size is a unit shouting as loudly as the figure it
 * qualifies. Splitting on whitespace and dropping the non-numeric tokens to heading size
 * in the muted ink is what makes the number the object — which is the one thing the old
 * instrument-panel treatment got right, kept here without the 40pt monospace face that
 * came with it.
 *
 * A plain number takes the fast path and is one `<AppText>`, unchanged.
 */
function MetricValue({
  value,
  compact,
  color,
}: {
  value: string;
  compact: boolean;
  color?: string;
}) {
  const p = useTheme();
  const variant = compact ? 'metricSm' : 'metric';
  const tokens = value.split(/\s+/).filter(Boolean);
  const hasUnits = tokens.length > 1 && tokens.some(tk => !/\d/.test(tk));

  if (!hasUnits) {
    return (
      <AppText variant={variant} color={color} numberOfLines={1}>
        {value}
      </AppText>
    );
  }

  return (
    <View style={styles.figure}>
      {tokens.map((tk, i) =>
        /\d/.test(tk) ? (
          <AppText key={i} variant={variant} color={color}>
            {tk}
          </AppText>
        ) : (
          <AppText
            key={i}
            variant={compact ? 'caption' : 'body'}
            color={p.mutedForeground}
            style={styles.unit}>
            {tk}
          </AppText>
        ),
      )}
    </View>
  );
}

export type StatTileProps = {
  label: string;
  value: number | string;
  icon?: IconName;
  tone?: StatTone;
  /** The line under the value — "из 27 терминалов", "за последние 7 дней". */
  hint?: string | null;
  /** Colour override for the hint. Only ever the bad news: an all-quiet board is grey. */
  hintColor?: string;
  /** A ratio bar under the value. Pass with `meterMax`. */
  meter?: number;
  meterMax?: number;
  /** A twelve-point trend, right-aligned inside the tile. */
  trend?: number[];
  /** @deprecated Число в карточке теперь всегда 24pt — как у карточек главной. */
  compact?: boolean;
  loading?: boolean;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function StatTile({
  label,
  value,
  icon,
  tone = 'neutral',
  hint,
  hintColor,
  meter,
  meterMax,
  trend,
  loading = false,
  onPress,
  style,
  testID,
}: StatTileProps) {
  const p = useTheme();
  const t = skin(p, tone);
  const shown = typeof value === 'number' ? compactNumber(value) : value;

  const body = (
    <>
      <View style={styles.head}>
        {/* Two lines allowed: «Устройств онлайн» does not fit a half-width tile beside
            its glyph, and a label that ends in an ellipsis has stopped being one. */}
        <AppText variant="eyebrow" numberOfLines={2} style={styles.label}>
          {label}
        </AppText>
        {/* Значок в углу плитки — быстрый ориентир, о чём число. */}
        {icon ? <Icon name={icon} size={15} color={t.glyph} /> : null}
      </View>

      <View style={styles.figures}>
        <View style={styles.value}>
          {loading ? (
            <Skeleton height={24} width="60%" radius={radii.sm} />
          ) : (
            <MetricValue
              value={shown}
              compact
              color={tone === 'neutral' ? undefined : t.glyph}
            />
          )}
          {hint ? (
            <AppText variant="micro" color={hintColor} numberOfLines={1} style={styles.hint}>
              {hint}
            </AppText>
          ) : null}
        </View>
        {trend && trend.length > 1 && !loading ? (
          <Sparkbars points={trend} tone={tone} />
        ) : null}
      </View>

      {meter != null && meterMax != null ? (
        <Meter value={meter} max={meterMax} tone={tone} />
      ) : null}
    </>
  );

  const surface: ViewStyle[] = [
    styles.tile,
    {backgroundColor: p.card, borderColor: p.border, borderLeftColor: t.mark},
  ];

  if (!onPress) {
    return (
      <View testID={testID} style={[...surface, style]}>
        {body}
      </View>
    );
  }

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${shown}`}
      onPress={onPress}
      style={state => [
        ...surface,
        // Плитка ведёт в раздел: под курсором — заливка наведения и рамка плотнее.
        (state.pressed || (state as {hovered?: boolean}).hovered === true) && {
          backgroundColor: p.elevated,
          borderColor: p.borderStrong,
          borderLeftColor: t.mark,
        },
        style,
      ]}>
      {body}
    </Pressable>
  );
}
